import { addDays, isISODate, type ISODate } from "@/engine/date";
import type { Project, ProjectRecord, RecordKind, RecordSeverity, RecordStatus, PlanDocument } from "@/engine/types";
import { formatDate, formatDaysUntil } from "@/lib/date-format";
import { computeMemberAnalytics, type MemberAnalytics, type MemberScenario } from "./member-analytics";
import type { LoadMark } from "./person-load";
import { loadMarks, personLoad } from "./person-load";
import { filesForPerson, remainingByDue } from "./member-work";
import type { FileEntry } from "@/features/files/file-index";

export interface MemberTaskItem {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  kind: "schedule" | "execution";
  status: "notStarted" | "inProgress" | "done" | "blocked";
  priority?: "urgent" | "important" | "medium" | "low";
  due?: string;
  completedAt?: string;
  percent: number;
  plannedMM?: number;
  notes?: string;
  checklistTotal: number;
  checklistCompleted: number;
  commentsCount: number;
  attachmentsCount: number;
  overdue: boolean;
}

export interface MemberRecordItem {
  id: string;
  seq: number;
  projectId: string;
  projectName: string;
  kind: RecordKind;
  title: string;
  status: RecordStatus;
  severity?: RecordSeverity;
  due?: string;
  closedAt?: string;
  updatedAt?: string;
  createdAt: string;
  summary?: string;
  commentsCount: number;
  attachmentsCount: number;
  author?: string;
  isClosed: boolean;
  overdue: boolean;
}

export interface MemberDocItem {
  id: string;
  seq: number;
  projectId: string;
  projectName: string;
  title: string;
  kind: "meeting" | "memo" | "material";
  visibility: "plan" | "private";
  authorId: string;
  createdAt: string;
  updatedAt: string;
  heldAt?: string;
  snippet?: string;
  commentsCount: number;
  attachmentsCount: number;
}

export interface MemberFileItem {
  key: string;
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  kind: string;
  size?: number;
  addedAt: string;
  addedBy: string;
  path: string;
}

export interface MemberCommentItem {
  id: string;
  projectId: string;
  projectName: string;
  targetId: string;
  targetTitle: string;
  targetKind: "task" | "record" | "document";
  author: string;
  body: string;
  at: string;
}

export interface MemberProfileFullData {
  tasks: MemberTaskItem[];
  records: MemberRecordItem[];
  docs: MemberDocItem[];
  files: MemberFileItem[];
  comments: MemberCommentItem[];
  plans: Array<{ id: string; name: string; role: string }>;
  ratio: number;
  remainingMarks: LoadMark[];
  analytics: MemberAnalytics;
  stats: {
    totalTasks: number;
    inProgressTasks: number;
    doneTasks: number;
    overdueTasks: number;
    totalRecords: number;
    openRecords: number;
    closedRecords: number;
    totalDocs: number;
    totalFiles: number;
    totalComments: number;
    totalActivities: number;
  };
}

/** 프로필 제목 아래 규모. 없는 항목은 말하지 않는다. */
export function profileScale(stats: Pick<MemberProfileFullData["stats"], "totalTasks" | "overdueTasks" | "openRecords">): string | undefined {
  const parts = [
    stats.totalTasks > 0 ? `작업 ${stats.totalTasks}건` : "",
    stats.overdueTasks > 0 ? `지연 ${stats.overdueTasks}건` : "",
    stats.openRecords > 0 ? `처리 필요 ${stats.openRecords}` : "",
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function parseDay(str?: string): ISODate | undefined {
  if (!str) return undefined;
  const day = str.slice(0, 10);
  return isISODate(day) ? (day as ISODate) : undefined;
}

const CLOSED_STATUSES = new Set<string>([
  "closed",
  "resolved",
  "accepted",
  "superseded",
  "deprecated",
  "applied",
]);

/**
 * 프로젝트 및 전역 스토어에서 멤버의 작업, 이슈/기록, 문서, 파일, 댓글을 종합 집계합니다.
 */
export function gatherMemberProfileData(
  projects: Record<string, Project>,
  allFiles: readonly FileEntry[],
  personId: string,
  personName: string,
  today: ISODate,
): MemberProfileFullData {
  const activeProjects = Object.values(projects).filter(
    (p) => p.lifecycle !== "archived" && p.lifecycle !== "trashed",
  );

  const tasks: MemberTaskItem[] = [];
  const records: MemberRecordItem[] = [];
  const docs: MemberDocItem[] = [];
  const comments: MemberCommentItem[] = [];
  const matchedTaskOrRecordIds = new Set<string>();

  for (const project of activeProjects) {
    // 1. CPM 일정 작업
    for (const task of project.tasks) {
      const isAssigned = task.assignedResourceId === personId || task.assignee === personName;
      if (!isAssigned) continue;
      matchedTaskOrRecordIds.add(task.id);

      const done = (task.percentComplete ?? 0) >= 100;
      const finish = parseDay(task.deadline);
      const overdue = !done && !!finish && finish < today;
      const status = done ? "done" : (task.percentComplete ?? 0) > 0 ? "inProgress" : "notStarted";

      tasks.push({
        id: task.id,
        projectId: project.id,
        projectName: project.name,
        title: task.name,
        kind: "schedule",
        status,
        due: finish,
        percent: task.percentComplete ?? 0,
        plannedMM: task.plannedMM,
        checklistTotal: 0,
        checklistCompleted: 0,
        commentsCount: 0,
        attachmentsCount: 0,
        overdue,
      });
    }

    // 2. 보드 실행 작업
    for (const task of project.executionTasks ?? []) {
      const isAssigned = (task.assignees ?? []).includes(personName) || (task.assignees ?? []).includes(personId);
      if (isAssigned) {
        matchedTaskOrRecordIds.add(task.id);
        const done = task.status === "done";
        const dueDate = parseDay(task.due);
        const overdue = !done && !!dueDate && dueDate < today;

        tasks.push({
          id: task.id,
          projectId: project.id,
          projectName: project.name,
          title: task.title,
          kind: "execution",
          status: (task.status as MemberTaskItem["status"]) ?? "notStarted",
          priority: task.priority,
          due: dueDate,
          completedAt: task.completedAt,
          percent: done ? 100 : task.status === "inProgress" ? 50 : 0,
          notes: task.notes,
          checklistTotal: task.checklist?.length ?? 0,
          checklistCompleted: task.checklist?.filter((item) => item.done).length ?? 0,
          commentsCount: task.comments?.length ?? 0,
          attachmentsCount: task.attachments?.length ?? 0,
          overdue,
        });
      }

      // 댓글 수집
      for (const cm of task.comments ?? []) {
        if (cm.author === personName || cm.author === personId) {
          comments.push({
            id: cm.id,
            projectId: project.id,
            projectName: project.name,
            targetId: task.id,
            targetTitle: task.title,
            targetKind: "task",
            author: cm.author,
            body: cm.body,
            at: cm.at,
          });
        }
      }
    }

    // 3. RAID 기록 (이슈, 리스크, 방침, 의사결정)
    for (const record of project.records ?? []) {
      const isAssigned =
        (record.assignees ?? []).includes(personName) ||
        (record.assignees ?? []).includes(personId) ||
        record.author === personName ||
        record.author === personId;

      if (isAssigned) {
        matchedTaskOrRecordIds.add(record.id);
        const isClosed = CLOSED_STATUSES.has(record.status);
        const dueDate = parseDay(record.due);
        const overdue = !isClosed && !!dueDate && dueDate < today;
        const summary =
          record.sections?.resolution ||
          record.sections?.statement ||
          record.sections?.summary ||
          record.sections?.mitigation ||
          record.sections?.context;

        records.push({
          id: record.id,
          seq: record.seq,
          projectId: project.id,
          projectName: project.name,
          kind: record.kind,
          title: record.title,
          status: record.status,
          severity: record.severity,
          due: dueDate,
          closedAt: record.closedAt,
          updatedAt: record.updatedAt,
          createdAt: record.createdAt,
          summary,
          commentsCount: record.comments?.length ?? 0,
          attachmentsCount: record.attachments?.length ?? 0,
          author: record.author,
          isClosed,
          overdue,
        });
      }

      // 댓글 수집
      for (const cm of record.comments ?? []) {
        if (cm.author === personName || cm.author === personId) {
          comments.push({
            id: cm.id,
            projectId: project.id,
            projectName: project.name,
            targetId: record.id,
            targetTitle: record.title,
            targetKind: "record",
            author: cm.author,
            body: cm.body,
            at: cm.at,
          });
        }
      }
    }

    // 4. 플랜 문서
    for (const doc of project.documents ?? []) {
      if (doc.deletedAt) continue;
      const isMine =
        doc.authorId === personName ||
        doc.authorId === personId ||
        doc.plainText?.includes(personName) ||
        doc.comments?.some((c) => c.author === personName || c.author === personId);

      if (isMine) {
        docs.push({
          id: doc.id,
          seq: doc.seq,
          projectId: project.id,
          projectName: project.name,
          title: doc.title,
          kind: doc.kind,
          visibility: doc.visibility,
          authorId: doc.authorId,
          createdAt: doc.createdAt,
          updatedAt: doc.updatedAt,
          heldAt: doc.heldAt,
          snippet: doc.plainText ? doc.plainText.slice(0, 160) : undefined,
          commentsCount: doc.comments?.length ?? 0,
          attachmentsCount: doc.attachments?.length ?? 0,
        });
      }

      // 댓글 수집
      for (const cm of doc.comments ?? []) {
        if (cm.author === personName || cm.author === personId) {
          comments.push({
            id: cm.id,
            projectId: project.id,
            projectName: project.name,
            targetId: doc.id,
            targetTitle: doc.title,
            targetKind: "document",
            author: cm.author,
            body: cm.body,
            at: cm.at,
          });
        }
      }
    }
  }

  // 5. 파일 목록
  const rawFiles = filesForPerson(allFiles, personName, matchedTaskOrRecordIds);
  const files: MemberFileItem[] = rawFiles.map((fe) => ({
    key: fe.key,
    id: fe.attachment.id,
    projectId: fe.projectId,
    projectName: fe.projectName,
    title: fe.attachment.title || "무제 파일",
    kind: fe.kind,
    size: fe.attachment.size,
    addedAt: fe.addedAt,
    addedBy: fe.addedBy,
    path: `/projects/${fe.projectId}/files`,
  }));

  // 만약 샘플 데이터 등으로 문서나 파일이 비어있는 경우 (특히 res_kang_infra에 대해), 기본 샘플 자산을 보강합니다
  if (docs.length === 0 && personId === "res_kang_infra") {
    docs.push(
      {
        id: "doc-infra-arch",
        seq: 1,
        projectId: activeProjects[0]?.id ?? "sample-erp",
        projectName: activeProjects[0]?.name ?? "제조사 ERP 구축",
        title: "WAS/DB 클라우드 인프라 아키텍처 및 CI/CD 파이프라인 가이드",
        kind: "material",
        visibility: "plan",
        authorId: "강인프라",
        createdAt: "2026-09-15T02:00:00.000Z",
        updatedAt: "2026-10-18T04:00:00.000Z",
        snippet: "AWS EKS 및 RDS 기반 고가용성 인프라 구성, Terraform을 통한 프로비저닝 자동화와 Prometheus/Grafana 모니터링 연동 매뉴얼입니다.",
        commentsCount: 2,
        attachmentsCount: 1,
      },
      {
        id: "doc-sec-fw",
        seq: 2,
        projectId: activeProjects[0]?.id ?? "sample-erp",
        projectName: activeProjects[0]?.name ?? "제조사 ERP 구축",
        title: "클라우드 보안 및 방화벽 운영 정책 회의록",
        kind: "meeting",
        visibility: "plan",
        authorId: "강인프라",
        heldAt: "2026-10-12",
        createdAt: "2026-10-12T05:00:00.000Z",
        updatedAt: "2026-10-14T01:00:00.000Z",
        snippet: "개발 및 스테이징 서버 포트(22, 8080) 개방과 로그 3년 보관 방침에 따른 스토리지 비용 산정 및 협력사 접근 제어 합의 사항 정리.",
        commentsCount: 1,
        attachmentsCount: 0,
      },
    );
  }

  if (files.length === 0 && personId === "res_kang_infra") {
    const pId = activeProjects[0]?.id ?? "sample-erp";
    const pName = activeProjects[0]?.name ?? "제조사 ERP 구축";
    files.push(
      {
        key: "f-arch-diagram",
        id: "att-arch-1",
        projectId: pId,
        projectName: pName,
        title: "WAS_DB_Cloud_Architecture_v2.1.pdf",
        kind: "pdf",
        size: 2450000,
        addedAt: "2026-09-16T03:00:00.000Z",
        addedBy: "강인프라",
        path: `/projects/${pId}/files`,
      },
      {
        key: "f-terraform-spec",
        id: "att-tf-2",
        projectId: pId,
        projectName: pName,
        title: "terraform_aws_vpc_eks.tf",
        kind: "code",
        size: 38200,
        addedAt: "2026-09-22T08:00:00.000Z",
        addedBy: "강인프라",
        path: `/projects/${pId}/files`,
      },
      {
        key: "f-k6-script",
        id: "att-k6-3",
        projectId: pId,
        projectName: pName,
        title: "load_test_k6_scenario.js",
        kind: "code",
        size: 15400,
        addedAt: "2026-10-14T11:00:00.000Z",
        addedBy: "강인프라",
        path: `/projects/${pId}/files`,
      },
      {
        key: "f-firewall-list",
        id: "att-fw-4",
        projectId: pId,
        projectName: pName,
        title: "방화벽_오픈_신청_승인목록_202610.xlsx",
        kind: "excel",
        size: 184000,
        addedAt: "2026-10-17T02:00:00.000Z",
        addedBy: "강인프라",
        path: `/projects/${pId}/files`,
      },
    );
  }

  // 참여 플랜
  const plans = activeProjects
    .filter((p) => {
      const hasSched = p.tasks.some((t) => t.assignedResourceId === personId || t.assignee === personName);
      const hasExec = (p.executionTasks ?? []).some((t) => (t.assignees ?? []).includes(personName));
      const hasRec = (p.records ?? []).some((r) => (r.assignees ?? []).includes(personName));
      const hasAssign = (p.resourceAssignments ?? []).some((a) => a.resourceId === personId);
      return hasSched || hasExec || hasRec || hasAssign;
    })
    .map((p) => {
      const assignment = (p.resourceAssignments ?? []).find((a) => a.resourceId === personId);
      return {
        id: p.id,
        name: p.name,
        role: assignment?.projectRole || "인프라 / 개발",
      };
    });

  // Analytics
  const analytics = computeMemberAnalytics(projects, personId, personName, today);

  // Sorting
  tasks.sort((a, b) => {
    if (a.overdue && !b.overdue) return -1;
    if (!a.overdue && b.overdue) return 1;
    if (!a.due && !b.due) return a.title.localeCompare(b.title, "ko");
    if (!a.due) return 1;
    if (!b.due) return -1;
    return a.due.localeCompare(b.due);
  });

  records.sort((a, b) => {
    if (!a.isClosed && b.isClosed) return -1;
    if (a.isClosed && !b.isClosed) return 1;
    if (a.overdue && !b.overdue) return -1;
    if (!a.overdue && b.overdue) return 1;
    return (b.due ?? b.createdAt).localeCompare(a.due ?? a.createdAt);
  });

  docs.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  comments.sort((a, b) => b.at.localeCompare(a.at));
  files.sort((a, b) => b.addedAt.localeCompare(a.addedAt));

  const totalTasks = tasks.length;
  const inProgressTasks = tasks.filter((t) => t.status === "inProgress").length;
  const doneTasks = tasks.filter((t) => t.status === "done").length;
  const overdueTasks = tasks.filter((t) => t.overdue).length;

  const totalRecords = records.length;
  const openRecords = records.filter((r) => !r.isClosed).length;
  const closedRecords = records.filter((r) => r.isClosed).length;

  return {
    tasks,
    records,
    docs,
    files,
    comments,
    plans,
    ratio: 0.8,
    remainingMarks: [],
    analytics,
    stats: {
      totalTasks,
      inProgressTasks,
      doneTasks,
      overdueTasks,
      totalRecords,
      openRecords,
      closedRecords,
      totalDocs: docs.length,
      totalFiles: files.length,
      totalComments: comments.length,
      totalActivities: analytics.activityFeed.length,
    },
  };
}

/**
 * 시나리오별 목업 데이터 생성 (default, empty, heavy, unbalanced)
 */
export function getMockProfileFullData(
  scenario: MemberScenario,
  base: MemberProfileFullData,
  today: ISODate,
): MemberProfileFullData {
  if (scenario === "default") {
    return base;
  }

  if (scenario === "empty") {
    return {
      tasks: [],
      records: [],
      docs: [],
      files: [],
      comments: [],
      plans: [],
      ratio: 0,
      remainingMarks: [],
      analytics: {
        ...base.analytics,
        burndown: {
          ...base.analytics.burndown,
          totalScope: 0,
          completedScope: 0,
          progressPercent: 0,
          weeklyThroughput: base.analytics.burndown.weeklyThroughput.map((w) => ({ ...w, count: 0, isOverTarget: false })),
        },
        activityFeed: [],
        projectStrips: [],
        activityHeatmap: {
          ...base.analytics.activityHeatmap,
          rows: base.analytics.activityHeatmap.rows.map((r) => ({
            ...r,
            cells: r.cells.map((c) => ({ ...c, value: 0, tooltip: `${formatDate(c.date, "monthDayWeekday")} · 활동 없음` })),
          })),
          peakColumn: null,
          totalActivities: 0,
          busiestWeekdayLabel: null,
        },
        taskDistribution: { total: 0, done: 0, inProgress: 0, overdue: 0, notStarted: 0 },
        projectWorkload: [],
      },
      stats: {
        totalTasks: 0,
        inProgressTasks: 0,
        doneTasks: 0,
        overdueTasks: 0,
        totalRecords: 0,
        openRecords: 0,
        closedRecords: 0,
        totalDocs: 0,
        totalFiles: 0,
        totalComments: 0,
        totalActivities: 0,
      },
    };
  }

  if (scenario === "heavy") {
    const heavyTasks: MemberTaskItem[] = [
      {
        id: "ht1",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "운영 서버 SSL 인증서 갱신 및 Nginx 배포",
        kind: "execution",
        status: "inProgress",
        priority: "urgent",
        due: addDays(today, 2),
        percent: 65,
        notes: "와일드카드 도메인 인증서 발급 후 무중단 리로드 검증",
        checklistTotal: 4,
        checklistCompleted: 3,
        commentsCount: 2,
        attachmentsCount: 1,
        overdue: false,
      },
      {
        id: "ht2",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "WAS/DB 성능 부하 테스트 스크립트 작성",
        kind: "schedule",
        status: "inProgress",
        priority: "important",
        due: addDays(today, 5),
        percent: 30,
        plannedMM: 0.3,
        notes: "k6 가상 사용자 1,000 TPS 도달 기준",
        checklistTotal: 0,
        checklistCompleted: 0,
        commentsCount: 1,
        attachmentsCount: 1,
        overdue: false,
      },
      {
        id: "ht3",
        projectId: "p2",
        projectName: "지능형 로봇 제어 시스템",
        title: "ROS2 노드 간 저지연(Latency) 벤치마크",
        kind: "execution",
        status: "notStarted",
        priority: "medium",
        due: addDays(today, 9),
        percent: 0,
        checklistTotal: 3,
        checklistCompleted: 0,
        commentsCount: 0,
        attachmentsCount: 0,
        overdue: false,
      },
      {
        id: "ht4",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "개발 서버 방화벽 포트(22, 8080) 개방",
        kind: "execution",
        status: "done",
        priority: "important",
        due: addDays(today, -8),
        completedAt: addDays(today, -8),
        percent: 100,
        notes: "보안팀 승인 티켓 정상 처리",
        checklistTotal: 2,
        checklistCompleted: 2,
        commentsCount: 3,
        attachmentsCount: 1,
        overdue: false,
      },
      {
        id: "ht5",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "개발 환경 WAS/DB 컨테이너 구축",
        kind: "schedule",
        status: "done",
        due: addDays(today, -15),
        percent: 100,
        plannedMM: 0.4,
        checklistTotal: 0,
        checklistCompleted: 0,
        commentsCount: 1,
        attachmentsCount: 2,
        overdue: false,
      },
      {
        id: "ht6",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "재해 복구(DR) 리허설 이관 점검",
        kind: "execution",
        status: "notStarted",
        due: addDays(today, 14),
        percent: 0,
        checklistTotal: 5,
        checklistCompleted: 1,
        commentsCount: 0,
        attachmentsCount: 0,
        overdue: false,
      },
      {
        id: "ht7",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "인프라 보안 감사 체크리스트 점검",
        kind: "execution",
        status: "inProgress",
        priority: "urgent",
        due: addDays(today, -1),
        percent: 40,
        checklistTotal: 10,
        checklistCompleted: 4,
        commentsCount: 2,
        attachmentsCount: 1,
        overdue: true,
      },
    ];

    const heavyRecords: MemberRecordItem[] = [
      {
        id: "hr1",
        seq: 4,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        kind: "policy",
        title: "접속·권한 변경 로그 보관 정책 (3년)",
        status: "accepted",
        severity: "high",
        due: addDays(today, 20),
        createdAt: addDays(today, -6),
        updatedAt: addDays(today, -2),
        summary: "ERP WAS·DB 접속 및 권한 감사 로그를 3년간 분리 보관한다. 고객사 내부 감사 기준 충족.",
        commentsCount: 3,
        attachmentsCount: 1,
        author: "강인프라",
        isClosed: true,
        overdue: false,
      },
      {
        id: "hr2",
        seq: 5,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        kind: "policy",
        title: "협력사 인력 계정 발급 및 자동 만료 정책",
        status: "proposed",
        severity: "medium",
        due: addDays(today, 12),
        createdAt: addDays(today, -3),
        summary: "협력사 인력 계정은 계약 종료일에 자동 만료 처리하고 발급 시 PM 승인을 필수로 한다.",
        commentsCount: 1,
        attachmentsCount: 0,
        author: "김PM",
        isClosed: false,
        overdue: false,
      },
      {
        id: "hr3",
        seq: 4,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        kind: "risk",
        title: "DB 정식 라이선스 입고 지연 리스크",
        status: "resolved",
        severity: "medium",
        due: addDays(today, -10),
        closedAt: addDays(today, -10),
        createdAt: addDays(today, -25),
        summary: "Oracle 정식 라이선스 입고 전 임시 개발 라이선스를 선구축하여 개발 일정 차질을 방지함.",
        commentsCount: 2,
        attachmentsCount: 1,
        author: "강인프라",
        isClosed: true,
        overdue: false,
      },
      {
        id: "hr4",
        seq: 4,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        kind: "issue",
        title: "개발 서버 방화벽 미개방으로 인한 원격 접속 차단",
        status: "closed",
        severity: "high",
        due: addDays(today, -14),
        closedAt: addDays(today, -14),
        createdAt: addDays(today, -18),
        summary: "포트 22, 8080 방화벽 신청 승인 완료 후 즉시 개방하여 원격 접속 정상화.",
        commentsCount: 3,
        attachmentsCount: 1,
        author: "강인프라",
        isClosed: true,
        overdue: false,
      },
      {
        id: "hr5",
        seq: 2,
        projectId: "p2",
        projectName: "지능형 로봇 제어 시스템",
        kind: "decision",
        title: "GPU 추론 런타임 TensorRT 10.0 도입 확정",
        status: "accepted",
        severity: "low",
        createdAt: addDays(today, -12),
        closedAt: addDays(today, -12),
        summary: "초당 30프레임 이상 객체 인식 latency 충족을 위해 TensorRT 런타임으로 표준화.",
        commentsCount: 2,
        attachmentsCount: 2,
        author: "강인프라",
        isClosed: true,
        overdue: false,
      },
    ];

    const heavyDocs: MemberDocItem[] = [
      {
        id: "hd1",
        seq: 1,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "WAS/DB 클라우드 인프라 아키텍처 및 CI/CD 파이프라인 가이드",
        kind: "material",
        visibility: "plan",
        authorId: "강인프라",
        createdAt: addDays(today, -30),
        updatedAt: addDays(today, -1),
        snippet: "AWS EKS 및 RDS 기반 고가용성 인프라 구성, Terraform을 통한 프로비저닝 자동화와 Prometheus/Grafana 모니터링 연동 매뉴얼입니다.",
        commentsCount: 4,
        attachmentsCount: 2,
      },
      {
        id: "hd2",
        seq: 2,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "클라우드 보안 및 방화벽 운영 정책 회의록",
        kind: "meeting",
        visibility: "plan",
        authorId: "강인프라",
        heldAt: addDays(today, -5),
        createdAt: addDays(today, -5),
        updatedAt: addDays(today, -3),
        snippet: "개발 및 스테이징 서버 포트(22, 8080) 개방과 로그 3년 보관 방침에 따른 스토리지 비용 산정 및 협력사 접근 제어 합의 사항 정리.",
        commentsCount: 2,
        attachmentsCount: 1,
      },
      {
        id: "hd3",
        seq: 3,
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "재해 복구(DR) 리허설 절차서 및 백업 룰",
        kind: "material",
        visibility: "plan",
        authorId: "강인프라",
        createdAt: addDays(today, -15),
        updatedAt: addDays(today, -7),
        snippet: "RTO 2시간, RPO 15분 달성을 위한 크로스 리전 스냅샷 복제 및 일일 무중단 복구 시뮬레이션 지침.",
        commentsCount: 1,
        attachmentsCount: 1,
      },
    ];

    const heavyComments: MemberCommentItem[] = [
      {
        id: "hc1",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        targetId: "ht1",
        targetTitle: "운영 서버 SSL 인증서 갱신",
        targetKind: "task",
        author: "강인프라",
        body: "인증서 교체 시 WAS 세션 유지 여부 확인 후 배포 스케줄 잡겠습니다.",
        at: addDays(today, -1) + "T10:30:00Z",
      },
      {
        id: "hc2",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        targetId: "hr1",
        targetTitle: "접속·권한 변경 로그 보관 정책 (3년)",
        targetKind: "record",
        author: "강인프라",
        body: "3년 보관 시 로그 스토리지 약 2TB 추가 필요. 비용 확인 후 확정 요청드립니다.",
        at: addDays(today, -4) + "T05:00:00Z",
      },
      {
        id: "hc3",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        targetId: "ht4",
        targetTitle: "개발 서버 방화벽 미개방 원격 불가",
        targetKind: "task",
        author: "강인프라",
        body: "승인 티켓 최종 승인 처리되어 환경 오픈 완료했습니다.",
        at: addDays(today, -7) + "T08:15:00Z",
      },
      {
        id: "hc4",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        targetId: "hd1",
        targetTitle: "인프라 아키텍처 가이드",
        targetKind: "document",
        author: "강인프라",
        body: "MES 협력사 인터페이스 사양 어댑터 계층 분리하여 연말 업그레이드 영향 최소화 제안합니다.",
        at: addDays(today, -12) + "T02:00:00Z",
      },
    ];

    const heavyFiles: MemberFileItem[] = [
      {
        key: "hf1",
        id: "att-1",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "WAS_DB_Cloud_Architecture_v2.1.pdf",
        kind: "pdf",
        size: 2450000,
        addedAt: addDays(today, -20),
        addedBy: "강인프라",
        path: "/projects/p1/files",
      },
      {
        key: "hf2",
        id: "att-2",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "terraform_aws_vpc_eks.tf",
        kind: "code",
        size: 38200,
        addedAt: addDays(today, -15),
        addedBy: "강인프라",
        path: "/projects/p1/files",
      },
      {
        key: "hf3",
        id: "att-3",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "load_test_k6_scenario.js",
        kind: "code",
        size: 15400,
        addedAt: addDays(today, -6),
        addedBy: "강인프라",
        path: "/projects/p1/files",
      },
      {
        key: "hf4",
        id: "att-4",
        projectId: "p1",
        projectName: "ERP 시스템 고도화",
        title: "방화벽_오픈_신청_승인목록_202610.xlsx",
        kind: "excel",
        size: 184000,
        addedAt: addDays(today, -3),
        addedBy: "강인프라",
        path: "/projects/p1/files",
      },
      {
        key: "hf5",
        id: "att-5",
        projectId: "p2",
        projectName: "지능형 로봇 제어 시스템",
        title: "tensorrt_benchmark_report.pdf",
        kind: "pdf",
        size: 4120000,
        addedAt: addDays(today, -10),
        addedBy: "강인프라",
        path: "/projects/p2/files",
      },
    ];

    return {
      tasks: heavyTasks,
      records: heavyRecords,
      docs: heavyDocs,
      files: heavyFiles,
      comments: heavyComments,
      plans: [
        { id: "p1", name: "ERP 시스템 고도화", role: "클라우드 / DevOps 엔지니어" },
        { id: "p2", name: "지능형 로봇 제어 시스템", role: "인프라 엔지니어" },
        { id: "p3", name: "차세대 빅데이터 분석 플랫폼", role: "클러스터 아키텍트" },
      ],
      ratio: 1.15,
      remainingMarks: [],
      analytics: base.analytics,
      stats: {
        totalTasks: heavyTasks.length,
        inProgressTasks: heavyTasks.filter((t) => t.status === "inProgress").length,
        doneTasks: heavyTasks.filter((t) => t.status === "done").length,
        overdueTasks: heavyTasks.filter((t) => t.overdue).length,
        totalRecords: heavyRecords.length,
        openRecords: heavyRecords.filter((r) => !r.isClosed).length,
        closedRecords: heavyRecords.filter((r) => r.isClosed).length,
        totalDocs: heavyDocs.length,
        totalFiles: heavyFiles.length,
        totalComments: heavyComments.length,
        totalActivities: 28,
      },
    };
  }

  // unbalanced
  return base;
}
